<template>
  <div class="seatplan-view" ref="seatplanView">
    <div class="categorylist">
      <div  class="categorylist__item" v-for="cat in categoryList" :key="cat.ID">
        <div class="category-color" :style="`background-color: #${cat.Color}`"></div>
        <span class="price">{{ cat.Price | fmtcurrency }}</span>
      </div>
    </div>
    <div class="seat-count-picker">
      <label v-if="maxSeatsChooseable > 0">Anzahl der Sitzplätze:</label>
      <select v-model="count" v-if="maxSeatsChooseable > 0">
        <option :value="0">0</option>
        <option v-for="i in maxSeatsChooseable" :key="i" :value="i">{{ i }}</option>
      </select>

      <button class="btn btn-clearcart" v-show="cartCount > 0" @click.once.stop="$emit('clear')">gewählte {{ cartCount }} Plätze freigeben</button>
      <div class="expl-overlay--choose-seat" v-show="count < 1">
        Bitte wählen Sie die Anzahl der Plätze ⇪
      </div>
    </div>
    <div class="seatplan-wrapper" :class="{ checkoutmode }">

      <div class="expl-overlay" v-if="allInitialized">
          <div class="expl-overlay--checkout-info" v-if="checkoutmode && cartCount > 0">
            <p>Sie haben {{ cartCount }} Plätze gewählt</p>
            <button class="btn btn-black" @click.once.stop="createReservation()">Reservierung erstellen</button>
            <p>oder</p>
            <button class="btn" @click.stop="manualCheckoutoverwrite = true">Auswahl fortsetzen</button>
          </div>
      </div>

      <div v-show="allInitialized" ref="seatingplan" :class="{ selectionImpossible }" class="seating-plan" :style="`background-image: url(${seatPlanPicPath}); width: ${seatplanWidth}px; height:${seatplanHeight}px;`">
        <div>
          <div v-for="seat in availableSeats"  :key="seat.ID"
            @click.stop="handleSeatClick(seat)"
            @mouseenter="onMouseEnter(seat)"
            @mouseleave="onMouseLeave(seat)"
            :data-status="seat.Status"
            :data-type="seat.SeatType"
            class="seat" :class="{
              selected: seat.selected,
              spacer: seat.spacer,
              hoveredSpacer: seat.hoveredSpacer,
              hoveredSelected: seat.hoveredSelected,
              cornerSeat: seat.SeatType === 1,
              highlight: seat.highlight
            }"
            :style="`left: ${seat.posX * scaleFactor}px; top:${seat.posY * scaleFactor}px;  background-color: ${catColorMap[seat.CatID]}; box-shadow: 0 0 4px 0 ${catColorMap[seat.CatID]};`">
          </div>
          <div v-for="seat in cartSeats"  :key="`ìc-${seat.ID}`"
            class="seat selected incart" :class="{
              spacer: seat.SalesConditionID == SPACER_SALESCONDITION,
            }"
            @mouseenter.stop="() =>{}"
            @mouseleave.stop="() =>{}"
            :style="`left: ${seat.posX * scaleFactor}px; top:${seat.posY * scaleFactor}px;  background-color: ${catColorMap[seat.CatID]}; box-shadow: 0 0 4px 0 ${catColorMap[seat.CatID]};`">
          </div>
        </div>
      </div>

      <spinner v-if="!allInitialized"></spinner>
    </div>
    <div class="actions-container">
      <button class="btn-back" @click.once.stop="$emit('cancel')" v-if="!areSeatsSelected">Abbrechen</button>
      <button class="btn-back cancel" @click.once.stop="resetSeats()" v-else>Auswahl aufheben</button>
      <button class="btn-next" v-if="areSeatsSelected" @click.once.stop="submit()">Plätze Reservieren</button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import axios from 'axios'
import Spinner from '@/components/Spinner.vue'
import debounce from 'lodash/debounce'
import cloneDeep from 'lodash/cloneDeep'

import PinchZoom from '@/utils/zoom.js'

import {isTouchDevice} from '@/utils/helper.js'

const MAX_DISTANCE = 30

const isCornerSeat = s => s && s.SeatType === 1
const isFree = s => s && s.Status === 0
const ascByID = (a, b) => a.ID - b.ID
const ascByPrio = (a, b) => a.Priority - b.Priority
const ascBySeatNr = (a, b) => parseInt(a.SeatNr, 10) - parseInt(b.SeatNr, 10)
const justIDs = a => a.ID
const getDistance = (a, b) => Math.sqrt(Math.pow(parseInt(a.SeatNr, 10) - parseInt(b.SeatNr, 10),2))
const logSeatTypes = (seats = [])  => {
  console.log(seats.map(i => i.SeatType == 1 ? 'E': 'X').join(' | '))
}
const logSpacer = (seats = [])  => {
  console.log(seats.map(i => i.spacer ? 'S': 'X').join(' | '))
}

const logSeats = (seats = [], label = "seats")  => {
  console.table(seats.map(i => {
    const {ID, CatID, Row, SeatNr, SectorID, Priority, SeatType} = i
    return {Priority, ID, CatID, Row, SeatNr, SectorID, SeatType}
  }))
}

const validTiedSeat = (cSeat, nSeat) => {
  let nr = parseInt(cSeat.SeatNr, 10)
  return [nr + 1, nr -1].indexOf(parseInt(nSeat.SeatNr, 10)) !== -1
}

const allTied = a => {
  let valid = true
  a.forEach((s, idx) => {
    if(valid && a[idx+1] && !validTiedSeat(s, a[idx+1])) {
      valid = false
    }
  })
  return valid
}

const noSpacerSectors  = []
const seatInNoSpacerNeededSector = seat => {
  return noSpacerSectors.indexOf(seat.SectorID) !== -1
}

export default {
  components: { Spinner },
  props: {
    event: Number,
    categoryList: Array,
    seatsInCart: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data() {
    return {
      seatPlanPicPath : '',
      seatplanWidth: 800,
      seatplanHeight: 800,
      availableSeats: [],
      seats: [],
      seatplanLoaded: false,
      scaleFactor: 1,
      allInitialized: false,
      count: 0,
      selectionImpossible: false,
      manualCheckoutoverwrite: false,
    }
  },
  computed: {
    ...mapState(['API','CHECKOUT_PAGE', 'HALLPIC_HOST', 'SPACER_SALESCONDITION']),
    catColorMap(){
      return this.categoryList.reduce((acc, cur) => {
        acc[cur.ID] = `#${cur.Color}`
        return acc;
      }, {})
    },
    loadingComplete() {
      return this.seatplanLoaded //&& this.seats.length > 0
    },
    seatsMap() {
      return this.seats.reduce((acc, cur) => {
        acc[cur.ID] = cur
        return acc
      }, {})
    },
    maxSeatsChooseable() {
      return 10 - this.seatsInCart.length - 1
    },
    cartSeats() {
      return this.seatsInCart.map(s => {
        const seat = cloneDeep(this.seatsMap[s.ID])
        if (!seat) {
          return null
        }
        seat.incart = true;
        seat.SalesConditionID = s.SalesConditionID
        return seat
      }).filter(i => i !== null)
    },
    cartCount() {
      return this.seatsInCart.filter(s => s.SalesConditionID === 0).length
    },
    areSeatsSelected(){
      return this.availableSeats.filter(s => s.selected).length > 0
    },
    checkoutmode(){
      return this.count <= 0 && !this.manualCheckoutoverwrite
    }
  },
  watch: {
    loadingComplete(allLoaded) {
      if (allLoaded) {
        this.allInitialized = true;
        try {
          if (isTouchDevice()) {
            this.$nextTick(this.initZoom.bind(this))
          }
        } catch (e) {
          // some error
        }
      }
    },
    seatsInCart() {
      this.debouncedReloadStatus()
    },
    event(newEvent){
      this.init()
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    onMouseLeave(seat) {
      this.resetHoverStates()
    },
    onMouseEnter(seat) {
      this.resetHoverStates()
      const tiedSeats = this.tiedSeatsTo(seat, this.count)
      this.selectionImpossible = tiedSeats.length < this.count
      if (!this.selectionImpossible) {
        tiedSeats.forEach(s => { s.hoveredSelected = true; })
        this.applyHoverStates(tiedSeats)
      }
    },
    handleSeatClick(seat) {
      this.resetSeats(true)
      const tiedSeats = this.tiedSeatsTo(seat, this.count)
      this.selectionImpossible = tiedSeats.length < this.count
      if (!this.selectionImpossible) {
        tiedSeats.forEach(s => {
          s.chosen = true
        })
        this.selectSeats(tiedSeats)
      }
    },
    initZoom() {
      new PinchZoom(this.$refs.seatingplan, {
        draggableUnzoomed: false,
        tapZoomFactor: 6,
        minZoom:1
      });
    },
    submit() {
      const spacer = this.seats.filter(s => s.spacer && s.selected).map(justIDs)
      const seats = this.seats.filter(s => s.selected).map(justIDs)

      this.$emit('submit', {
        type: 'preferredSeats',
        seats, spacer
      })
    },
    sameRowAndSector(seat) {
      if (!seat) return []
      return this.availableSeats.filter(s => s.Row == seat.Row )
    },
    tiedSeatsTo(seat, Count, depth = 0) {
      if (Count === 0 || !seat) return []
      let tiedSeats = this._tiedSeatsTo(seat, Count)

      if ((tiedSeats.length === 0 || this.getAvailablePrevSeat(seat)) &&  depth < 2) {
        if (tiedSeats.length >= Count && isCornerSeat(seat)) {
          // * do not try iteration if we started from cornerseat
        } else {
          return this.tiedSeatsTo(this.getAvailablePrevSeat(seat), Count, depth + 1)
        }
      }

      const lastSeat = tiedSeats[tiedSeats.length -1]
      const firstSeat = tiedSeats[0]

      // * PUSH TO RIGHT CORNER IF POSSIBLE, PREVENTS UNUSABLE SEAT ON RIGHT EDGE
      if (lastSeat && lastSeat.spacer && isCornerSeat(lastSeat) && this.getAvailableNextSeat(seat)) {
        return this._tiedSeatsTo(this.getAvailableNextSeat(seat), Count)
      } else if (
        // * PUSH TO LEFT CORNER IF POSSIBLE, PREVENTS UNUSABLE SEAT ON LEFT EDGE
        firstSeat && !isCornerSeat(firstSeat) &&
        this.getAvailableNextSeat(seat) && isCornerSeat(this.getAvailablePrevSeat(seat))
      ) {
        return this._tiedSeatsTo(this.getAvailablePrevSeat(seat), Count)
      } else if (
        // * PUSH TO LEFT SELECTED IF POSSIBLE, PREVENTS UNUSABLE SEAT ON LEFT SIDE
        firstSeat && !isCornerSeat(firstSeat) &&
        this.getAvailableNextSeat(seat) && !this.getAvailablePrevSeat(seat, 2) && this.getAvailablePrevSeat(seat, 1)
      ) {
        return this._tiedSeatsTo(this.getAvailablePrevSeat(seat, 1), Count)
      } else if (
        // * PUSH TO LEFT SELECTED IF POSSIBLE, PREVENTS UNUSABLE SEAT ON LEFT SIDE
        lastSeat && !isCornerSeat(lastSeat) &&
        this.getAvailableNextSeat(lastSeat) &&
        this.getAvailablePrevSeat(firstSeat) &&
        !isCornerSeat(firstSeat)
      ) {
        return this._tiedSeatsTo(this.getAvailableNextSeat(seat, 1), Count)
      }

      return tiedSeats
    },
    _tiedSeatsTo(seat, Count) {
      if (Count < 1) return []
      const sameRow = cloneDeep(this.sameRowAndSector(seat))
      sameRow.forEach(s => {
        s.spacer = false
      })

      const MAP = sameRow.reduce((acc, cur) => {
        acc[cur.ID] = cur
        return acc
      }, {})

      sameRow.sort(ascByID);

      let startIdx = sameRow.findIndex(s => s.ID === seat.ID)
      let tiedSeats = sameRow.slice(startIdx, startIdx + Count)

      if (tiedSeats.length < Count) {
        // console.log('too less')
        return []
      }

      if (!allTied(tiedSeats)) {
        // console.log('not tied')
        return []
      }

      const cornerSeats = tiedSeats.filter(s => isCornerSeat(s))
      //* special handing if there ar emore than one corner seat in collection
      //* multiple cornerseats = corridor between or some special locations with only 2 seats in a row
      if (cornerSeats.length > 1) {
        const cornerSeatsInSameSector = new Set(cornerSeats.map(s => s.SectorID)).size === 1
        if (!cornerSeatsInSameSector) return [] // marked seats across corridor

        const cornerSeatsSameCategory = new Set(cornerSeats.map(s => s.CatID)).size === 1
        const cornerSeatDistance = getDistance(cornerSeats[0], cornerSeats[1])
        if (!cornerSeatsSameCategory & cornerSeatDistance <= 1) return []
      }

      const lastSeat = tiedSeats[tiedSeats.length - 1]

      if ( this.isRightCornerSeat(lastSeat)) {
        //* no spcer required
        // console.log("is right cornerseat")
        return tiedSeats
      }

      if (seatInNoSpacerNeededSector(seat)) {
        // console.log("no spacer needed")
        return tiedSeats
      }

      tiedSeats = sameRow.slice(startIdx, startIdx + Count + 1 /** ONE SPACER */)

      if (tiedSeats.length < Count + 1) {
        return []
      }

      if (!allTied(tiedSeats)) {
        return []
      }

      tiedSeats[tiedSeats.length-1].spacer = true

      return tiedSeats
    },
    isRightCornerSeat(seat = {}){
      if (!isCornerSeat(seat)) return false
      const nextSeat = this.seats.find(
        s => s.Row == seat.Row &&
        s.SectorID == seat.SectorID &&
        parseInt(s.SeatNr, 10) === (parseInt(seat.SeatNr, 10 ) + 1)
      )
      return !nextSeat || isCornerSeat(nextSeat)
    },
    getAvailablePrevSeat(seat = {}, distance = 1) {
      const prevSeat = this.availableSeats.find(
        s => s.Row == seat.Row &&
        s.SectorID == seat.SectorID &&
        parseInt(s.SeatNr, 10) === (parseInt(seat.SeatNr, 10 ) - distance)
      )

      if (prevSeat && distance == 2) {
        const middleSeat = this.availableSeats.find(
          s => s.Row == seat.Row &&
          s.SectorID == seat.SectorID &&
          parseInt(s.SeatNr, 10) === (parseInt(seat.SeatNr, 10 ) - 1)
        )
        if(!middleSeat) return
      }
      return prevSeat
    },
    getAvailableNextSeat(seat = {}, distance = 1) {
      const nextSeat = this.availableSeats.find(
        s => s.Row == seat.Row &&
        s.SectorID == seat.SectorID &&
        parseInt(s.SeatNr, 10) === (parseInt(seat.SeatNr, 10 ) + distance)
      )
      if (nextSeat && distance == 2) {
        const middleSeat = this.availableSeats.find(
          s => s.Row == seat.Row &&
          s.SectorID == seat.SectorID &&
          parseInt(s.SeatNr, 10) === (parseInt(seat.SeatNr, 10 ) + 1)
        )
        if(!middleSeat) return
      }
      return nextSeat
    },
    selectSeats(seats) {
      const map = this.availableSeats.reduce((acc, cur) =>  {
        acc[cur.ID] = cur
        return acc
      }, {})

      for (const seat of seats) {
        map[seat.ID].selected = true
        map[seat.ID].spacer = !!seat.spacer
      }
    },
    applyHoverStates(seats) {
      const map = this.availableSeats.reduce((acc, cur) =>  {
        acc[cur.ID] = cur
        cur.hoveredSelected = false
        cur.hoveredSpacer = false
        return acc
      }, {})

      for (const seat of seats) {
        if (seat.spacer) {
          map[seat.ID].hoveredSpacer = !!seat.spacer
        } else {
          map[seat.ID].hoveredSelected = true
        }
      }
    },
    resetHoverStates() {
      this.availableSeats.forEach(s => {
        s.hoveredSelected = false
        s.hoveredSpacer = false
        s.highlight = false
      })
    },
    async reloadStatus() {
      const {data: seats } = await axios.get(this.API, {
        params: {
          action: 'getEventSeatStatusList',
          eventId: this.event
        }
      })
      seats.forEach(s => {
        if (this.seatsMap[s.ID] !== undefined) {
          this.seatsMap[s.ID].Status = s.Status
        }
      })
      this.availableSeats = this.getAvailableSeats()
      this.count = 0
      this.manualCheckoutoverwrite = false
    },
    resetSeats( chosen ) {
      const filteredSeats = this.seats.filter(s => {
        if (chosen) return true
        return s.chosen !== true
      })

      for (const seat of filteredSeats) {
        seat.selected = false
        seat.spacer = false
        seat.chosen = false
        seat.highlight = false
      }
    },
    getAvailableSeats(){
      const inCartIds = this.seatsInCart.map(s => s.ID)
      const available = this.seats.filter(s => s.Status === 0 && inCartIds.indexOf(s.ID) === -1)
      available.forEach(s => {
        s.selected = false
        s.chosen = false
        s.highlight = false
        s.spacer = false
      })
      return available
    },
    createReservation() {
      this.$emit('createreservation')
    },
    async init() {
      this.allInitialized = false;
      const {data: seatplan } = await axios.get(this.API, {
        params: {
          action: 'getSaalPlanPic',
          fullsize: 1,
          eventId: this.event
        }
      })

      const img = new Image()
      img.onload = ((self) => {
        return function () {
          const containerWidth = self.$refs.seatplanView.offsetWidth;
          self.seatPlanPicPath = self.HALLPIC_HOST + seatplan.path;
          self.scaleFactor = (containerWidth/this.width)
          self.seatplanHeight = self.scaleFactor * this.height;
          self.seatplanWidth = containerWidth;
          self.seatplanLoaded = true;
        }
      })(this);
      img.src = this.HALLPIC_HOST + seatplan.path;

      const {data: seats } = await axios.get(this.API, {
        params: {
          action: 'getEventSeats',
          eventId: this.event
        }
      })

      this.seats = seats.map(s => {
        s.selected = false
        s.highlight= false
        s.spacer = false
        s.hoveredSpacer = false
        s.incart = false
        s.hoveredSelected = false
        return s
      });

      this.allInitialized = true

      this.debouncedReloadStatus()
    }
  },
  async created() {
    this.debouncedReloadStatus = debounce(this.reloadStatus.bind(this), 250)
    this.init()
  }
}
</script>


<style lang="scss" scoped>

.seatplan-view {
  display: grid;
  max-height: 90vh;
  min-height: 90vh;
  grid-template-rows: auto  48px 1fr auto;
}

.categorylist {
  display: flex;
  grid-template-columns: repeat(4, 1fr);
  grid-row: 1;
  /* autoprefixer: off */
  background-color: transparent;
  border-bottom: 1px solid black;
  padding: 8px;


  font-size: 18px;

  > *{
    align-self: center;
  }
}

@media only screen and (max-width: 640px){
  .categorylist {
    grid-template-columns: repeat(3, 1fr);

    :nth-child(1), :nth-child(4), :nth-child(7) {
      grid-column: 1;
    }
    :nth-child(2), :nth-child(5), :nth-child(8) {
      grid-column: 2;
    }
    :nth-child(3), :nth-child(6) {
      grid-column: 3;
    }
    :nth-child(4), :nth-child(5), :nth-child(6) {
      grid-row: 2;
    }
    :nth-child(7), :nth-child(8) {
      grid-row: 3;
    }
  }
}

.categorylist__item {
  display: grid;
  grid-template-columns: 2rem 80px;

  margin: 0 auto;

  > * {
    align-self: center;
  }

  .category-color {
    width: 1rem;
    height: 1rem;
    margin: 0 auto;
    border-radius: 50%;
    grid-column: 1;
  }
  .price {
    grid-column: 2;
  }
}

.seat-count-picker {
  position: relative;
  grid-row: 2;
  display: flex;
  align-items: center;
  padding: 0 16px;
  font-size: 20px;
  border-bottom: 1px solid black;
  text-transform: uppercase;
}


select {
  font-size: 20px;
  margin: 0 16px;
  padding: 4px 16px;
  border: 2px solid black;
}
.seatplan-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  grid-row: 3;
}

.seating-plan {
  top: 0;
  left: 0;
  position: absolute;
  width: 100%;
  height: 100%;
  background-size: cover;

  > div {
    // filter: blur(1px);
  }
}

button {
  background-color: goldenrod;
  border: 1px solid goldenrod;
  border-radius: 0;
  outline: none;
  cursor: pointer;
  font-size: 1.125rem;
  font-weight: 400;
  text-transform: uppercase;
  transition: all 0.35s ease-in-out;
  padding: 8px 16px;

  &:disabled {
    border: none;
    cursor: not-allowed;
    opacity: 0.7;
  }

  &.btn-black {
    background-color: black;
    color: white;
  }

  &.cancel {
    background-color: #666;
    color: white;
  }
}

.actions-container {
  padding: 0.5rem;
  display: flex;
  /* autoprefixer: off */
  background-color: white;
  grid-row: 4;
  border-top: 1px solid black;
  flex-direction: row;
  width: 100%;

  .btn-back {
    align-self: flex-start;
  }

  .btn-next {
    margin: 0 0 0 auto;
  }
}

.seat {
    width: 14px;
    height: 14px;
    border: none;
    border-radius: 50%;
    position: absolute;
    transform: translate(-50%, -50%);
    cursor: pointer;

    &.cornerSeat {
      border-radius: 0;
      border: 1px solid black;
    }

    &.selected {
      background-color: green!important;
      opacity: 1;
    }

    &.spacer {
      background: white!important;
      opacity: 1;
    }

    &.highlight {
      opacity: 0.5;
    }

    &.hoveredSelected::before {
      content: '';
      position: absolute;
      top: -2px;
      left: -2px;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background-color:green;
      opacity: 0.5;
      filter: blur(2px)
    }

    &.hoveredSpacer {
      opacity: 0;
    }

    &.incart {
      z-index: 1;
      cursor: pointer;
      &::before {
        content: '';
        position: absolute;
        top: -2px;
        left: -2px;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background-color:black;
        opacity: 0.3;
      }

      &.spacer {
        &::before{display: none;}
        background: white;
        opacity: 1;
      }
    }
  }

  @media only screen and (max-width: 640px){
    .seat {
      width: 6px;
      height: 6px;

      &.selected {
        &:before, &:after {
          height: 6px;
        }
      }
    }
  }


.seating-plan.selectionImpossible {
  .seat {
    cursor: not-allowed;
  }
}

button.btn-clearcart {
  margin: 0 0 0 auto;
  padding: 4px 16px;
  background-color: #666;
  color: white;
}


.seatplan-wrapper.checkoutmode {
  overflow: hidden;
  .expl-overlay {
    height: 100%;
  }
}


.expl-overlay {
  background-color: rgba(255,255,255,0.85);
  position: sticky;
  z-index: 10;
  width: 100%;
  height: 0;
  top: 0;
  overflow: hidden;
}

.expl-overlay--choose-seat {
  position: absolute;
  left: 40px;
  font-weight: bold;
  font-size: 16px;
  top: 48px;
  z-index: 100;
  text-transform: none;
  background-color: rgba(255,255,255,0.5);
  padding: 8px;
  border-radius: 4px;
}

.expl-overlay--checkout-info {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  font-size: 22px;
}

.expl-overlay--seating-info {
  color: #af0505;
}

</style>
